import type { ValidationError } from '@/services/validation';
import { WorkflowValidator } from '@/services/validation';
import type { Workflow } from '@/stores/workflowStore';
import { createMissionGoalNode, createMissionStartNode } from '@/lib/missionBlocks';
import { autoConfigureWorkflowNodes, getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';

export interface InlineValidationSuggestion {
  id: string;
  type: 'error' | 'warning';
  safety: 'safe-auto-fix' | 'review-first' | 'human-required';
  nodeId?: string;
  title: string;
  detail: string;
  affectedNodeIds: string[];
}

export interface InlineValidationState {
  failpointNodeIds: string[];
  disconnectedNodeIds: string[];
  suggestions: InlineValidationSuggestion[];
}

export interface InlineAutoFixResult {
  workflow: Workflow;
  changes: string[];
  safetySummary: {
    safeAutoFix: number;
    reviewFirst: number;
    humanRequired: number;
  };
}

function buildConnectivity(workflow: Workflow) {
  const nodeIds = new Set(workflow.nodes.map((n) => n.id));
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  workflow.nodes.forEach((node) => {
    outgoing.set(node.id, []);
    incoming.set(node.id, []);
  });

  workflow.edges.forEach((edge) => {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) return;
    outgoing.get(edge.from)?.push(edge.to);
    incoming.get(edge.to)?.push(edge.from);
  });

  const startIds = workflow.nodes.filter((n) => n.type === 'start').map((n) => n.id);
  const reachable = new Set<string>(startIds);
  const queue = [...startIds];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    for (const next of outgoing.get(nodeId) ?? []) {
      if (reachable.has(next)) continue;
      reachable.add(next);
      queue.push(next);
    }
  }

  return { outgoing, incoming, reachable };
}

function buildConnectivitySuggestions(workflow: Workflow): InlineValidationSuggestion[] {
  const { incoming, outgoing, reachable } = buildConnectivity(workflow);
  const disconnected = workflow.nodes
    .filter((node) => node.type !== 'start' && !reachable.has(node.id))
    .map((node) => node.id);
  if (disconnected.length === 0) return [];

  const disconnectedSet = new Set(disconnected);
  const failpoints = disconnected.filter((nodeId) =>
    (incoming.get(nodeId) ?? []).every((parentId) => !disconnectedSet.has(parentId))
  );

  return failpoints.map((nodeId) => {
    const impacted: string[] = [];
    const q = [nodeId];
    const seen = new Set<string>();
    while (q.length > 0) {
      const current = q.shift()!;
      if (seen.has(current)) continue;
      seen.add(current);
      impacted.push(current);
      for (const next of outgoing.get(current) ?? []) {
        if (disconnectedSet.has(next)) q.push(next);
      }
    }

    const node = workflow.nodes.find((n) => n.id === nodeId);
    const nodeLabel = node?.subtype ?? 'block';
    const impactedCount = Math.max(0, impacted.length - 1);
    return {
      id: `disconnect-${nodeId}`,
      type: 'error' as const,
      safety: 'safe-auto-fix' as const,
      nodeId,
      title: `Reconnect ${nodeLabel} to restore the mission chain`,
      detail:
        impactedCount > 0
          ? `${impactedCount} downstream block(s) are disconnected from START. Re-link this failpoint to its intended upstream step.`
          : 'This block is disconnected from START. Re-link it to an upstream step to restore execution.',
      affectedNodeIds: impacted,
    };
  }).sort((a, b) => a.id.localeCompare(b.id));
}

function mapValidationErrors(errors: ValidationError[]): InlineValidationSuggestion[] {
  return errors.map((err, idx) => ({
    id: `validation-${idx}-${err.nodeId ?? 'global'}`,
    type: err.type,
    safety: err.type === 'error' ? 'review-first' : 'safe-auto-fix',
    nodeId: err.nodeId,
    title: err.type === 'error' ? 'Validation error' : 'Validation warning',
    detail: err.message,
    affectedNodeIds: err.nodeId ? [err.nodeId] : [],
  }));
}

export function analyzeInlineValidation(workflow: Workflow): InlineValidationState {
  const connectivitySuggestions = buildConnectivitySuggestions(workflow);
  const errors = WorkflowValidator.validate(workflow);
  const validationSuggestions = mapValidationErrors(errors);

  const disconnectedNodeIds = new Set<string>();
  const failpointNodeIds = new Set<string>();
  connectivitySuggestions.forEach((suggestion) => {
    if (suggestion.nodeId) failpointNodeIds.add(suggestion.nodeId);
    suggestion.affectedNodeIds.forEach((nodeId) => disconnectedNodeIds.add(nodeId));
  });

  const smartInputSuggestions = workflow.nodes.flatMap((node) =>
    getSmartBlockInputRequirements(node).map((req, idx) => ({
      id: `smart-input-${node.id}-${idx}`,
      type: 'warning' as const,
      safety: 'human-required' as const,
      nodeId: node.id,
      title: `Smart Blocks needs ${req.label}`,
      detail: req.reason,
      affectedNodeIds: [node.id],
    }))
  );

  return {
    failpointNodeIds: [...failpointNodeIds],
    disconnectedNodeIds: [...disconnectedNodeIds],
    suggestions: [...connectivitySuggestions, ...validationSuggestions, ...smartInputSuggestions],
  };
}

function applyDefaultConfigFixes(workflow: Workflow, includeReviewLevel = true): string[] {
  const changes: string[] = [];
  workflow.nodes = workflow.nodes.map((node) => {
    if (node.type === 'tool' && node.subtype === 'http' && !String(node.config.url ?? '').trim()) {
      changes.push('Added missing HTTP URL placeholder');
      return { ...node, config: { ...node.config, url: 'https://example.com/api' } };
    }
    if (
      node.type === 'tool' &&
      node.subtype === 'transform' &&
      !String(node.config.transform ?? '').trim()
    ) {
      if (!includeReviewLevel) return node;
      changes.push('Added missing transform code');
      return { ...node, config: { ...node.config, transform: 'return input;' } };
    }
    if (node.type === 'action' && node.subtype === 'webhook' && !String(node.config.url ?? '').trim()) {
      changes.push('Added missing action webhook URL placeholder');
      return { ...node, config: { ...node.config, url: 'https://example.com/webhook' } };
    }
    if (node.type === 'trigger' && node.subtype === 'webhook' && !String(node.config.url ?? '').trim()) {
      changes.push('Added missing trigger webhook URL placeholder');
      return { ...node, config: { ...node.config, url: 'https://example.com/inbound' } };
    }
    if (
      node.type === 'agent' &&
      !String(node.config.agentBrief ?? node.config.contextPurpose ?? '').trim()
    ) {
      if (!includeReviewLevel) return node;
      changes.push('Added missing agent brief');
      return {
        ...node,
        config: { ...node.config, agentBrief: 'Analyze the input and choose the next best action.' },
      };
    }
    if (
      node.type === 'drone' &&
      !String(node.config.droneTask ?? node.config.contextPurpose ?? '').trim()
    ) {
      if (!includeReviewLevel) return node;
      changes.push('Added missing drone task');
      return { ...node, config: { ...node.config, droneTask: 'Execute remote mission step.' } };
    }
    if (node.type === 'goal' && node.subtype === 'outcome' && !String(node.config.outcome ?? '').trim()) {
      if (!includeReviewLevel) return node;
      changes.push('Filled missing GOAL outcome');
      return { ...node, config: { ...node.config, outcome: `Accomplish: ${workflow.name}` } };
    }
    return node;
  });
  return changes;
}

function dedupeAndCleanEdges(workflow: Workflow): string[] {
  const changes: string[] = [];
  const nodeIds = new Set(workflow.nodes.map((n) => n.id));
  const seen = new Set<string>();
  const nextEdges = [];
  for (const edge of workflow.edges) {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) {
      changes.push('Removed edge referencing a missing block');
      continue;
    }
    if (edge.from === edge.to) {
      changes.push('Removed self-loop edge');
      continue;
    }
    const key = `${edge.from}->${edge.to}`;
    if (seen.has(key)) {
      changes.push('Removed duplicate edge');
      continue;
    }
    seen.add(key);
    nextEdges.push(edge);
  }
  workflow.edges = nextEdges;
  return changes;
}

function connectStartIfNeeded(workflow: Workflow): string[] {
  const changes: string[] = [];
  const start = workflow.nodes.find((n) => n.type === 'start');
  if (!start) return changes;
  const hasOutgoing = workflow.edges.some((e) => e.from === start.id);
  if (hasOutgoing) return changes;
  const next =
    workflow.nodes
      .filter((n) => n.id !== start.id)
      .sort((a, b) => a.x - b.x || a.y - b.y)[0] ?? null;
  if (!next) return changes;
  workflow.edges.push({
    id: Math.random().toString(36).slice(2, 11),
    from: start.id,
    to: next.id,
  });
  changes.push('Connected START to the first mission step');
  return changes;
}

function reconnectDisconnectedFailpoints(workflow: Workflow): string[] {
  const changes: string[] = [];
  const { incoming, reachable } = buildConnectivity(workflow);
  const disconnected = workflow.nodes.filter((n) => n.type !== 'start' && !reachable.has(n.id));
  if (disconnected.length === 0) return changes;

  const disconnectedSet = new Set(disconnected.map((n) => n.id));
  const failpoints = disconnected.filter((n) =>
    (incoming.get(n.id) ?? []).every((parentId) => !disconnectedSet.has(parentId))
  );
  const reachableCandidates = workflow.nodes.filter((n) => reachable.has(n.id));
  if (reachableCandidates.length === 0) return changes;

  for (const failpoint of failpoints) {
    const orderedCandidates = [...reachableCandidates].sort((a, b) => {
      const da = Math.abs((a.x ?? 0) - (failpoint.x ?? 0)) + Math.abs((a.y ?? 0) - (failpoint.y ?? 0));
      const db = Math.abs((b.x ?? 0) - (failpoint.x ?? 0)) + Math.abs((b.y ?? 0) - (failpoint.y ?? 0));
      return da - db;
    });
    const source = orderedCandidates.find((n) => n.id !== failpoint.id);
    if (!source) continue;
    const exists = workflow.edges.some((e) => e.from === source.id && e.to === failpoint.id);
    if (exists) continue;
    workflow.edges.push({
      id: Math.random().toString(36).slice(2, 11),
      from: source.id,
      to: failpoint.id,
    });
    changes.push('Reconnected a disconnected failpoint');
  }
  return changes;
}

export function autoFixWorkflowInline(inputWorkflow: Workflow): InlineAutoFixResult {
  return autoFixWorkflowInlineWithOptions(inputWorkflow, true);
}

export function autoFixWorkflowInlineSafe(inputWorkflow: Workflow): InlineAutoFixResult {
  return autoFixWorkflowInlineWithOptions(inputWorkflow, false);
}

function autoFixWorkflowInlineWithOptions(
  inputWorkflow: Workflow,
  includeReviewLevel: boolean
): InlineAutoFixResult {
  const workflow: Workflow = {
    ...inputWorkflow,
    nodes: structuredClone(inputWorkflow.nodes),
    edges: structuredClone(inputWorkflow.edges),
    updatedAt: new Date(),
  };
  const changes: string[] = [];

  const starts = workflow.nodes.filter((n) => n.type === 'start');
  if (starts.length === 0) {
    const leftMostX = Math.min(...workflow.nodes.map((n) => n.x), 80);
    const topY = Math.min(...workflow.nodes.map((n) => n.y), 80);
    workflow.nodes.unshift(createMissionStartNode(Math.max(20, leftMostX - 220), topY));
    changes.push('Added missing START block');
  } else if (starts.length > 1) {
    const keep = [...starts].sort((a, b) => a.x - b.x || a.y - b.y)[0];
    const removeIds = new Set(starts.filter((s) => s.id !== keep.id).map((s) => s.id));
    workflow.nodes = workflow.nodes.filter((n) => !removeIds.has(n.id));
    workflow.edges = workflow.edges.filter((e) => !removeIds.has(e.from) && !removeIds.has(e.to));
    changes.push('Removed extra START blocks');
  }

  const goals = workflow.nodes.filter((n) => n.type === 'goal' && n.subtype === 'outcome');
  if (goals.length === 0 && includeReviewLevel) {
    const start = workflow.nodes.find((n) => n.type === 'start');
    workflow.nodes.push(
      createMissionGoalNode(
        `Accomplish: ${workflow.name}`,
        (start?.x ?? 80) + 220,
        start?.y ?? 80
      )
    );
    changes.push('Added missing GOAL block');
  }

  changes.push(...applyDefaultConfigFixes(workflow, includeReviewLevel));
  changes.push(...dedupeAndCleanEdges(workflow));
  changes.push(...connectStartIfNeeded(workflow));
  changes.push(...reconnectDisconnectedFailpoints(workflow));
  if (includeReviewLevel) {
    const configured = autoConfigureWorkflowNodes(workflow);
    if (configured.changes.length > 0) {
      workflow.nodes = configured.workflow.nodes;
      changes.push('Smart Blocks configured blocks from mission context');
    }
  }

  const safetySummary = {
    safeAutoFix: 0,
    reviewFirst: 0,
    humanRequired: 0,
  };
  changes.forEach((c) => {
    if (/placeholder|auto-configured|reconnected|removed|added missing/i.test(c)) safetySummary.safeAutoFix += 1;
    else if (/goal|agent|drone|transform/i.test(c)) safetySummary.reviewFirst += 1;
    else safetySummary.reviewFirst += 1;
  });
  const pendingHumanInputs = workflow.nodes.reduce(
    (acc, node) => acc + (getSmartBlockInputRequirements(node).length > 0 ? 1 : 0),
    0
  );
  safetySummary.humanRequired = pendingHumanInputs;

  return { workflow, changes, safetySummary };
}
